import React from "react";
import { motion } from "framer-motion";
import signatureBg from "../Assets/lakenriver7.webp";
import BackwaterCanoeing from "../Assets/BackwaterCanoeing.webp";
import ExploreImg from "../Assets/Explore.webp";
import RoomsImg from "../Assets/esc4-min-1-scaled.webp";
import PoolImg from "../Assets/pool3.webp";
import FoodImg from "../Assets/food2.webp";
import ActivityImg from "../Assets/outdoor-kayakk.webp";
import NaturalBlissImg from "../Assets/whychoose.webp";

const features = [
  {
    title: "Full Resort Access for Total Privacy",
    image: ExploreImg,
    items: [
      "No other guests – just your group and nature",
      "Private lawn, dining area, and hangout spaces",
    ],
  },
  {
    title: "9 Premium Rooms (Double Occupancy)",
    image: RoomsImg,
    items: ["4 Luxury Suite Rooms", "3 Lake View Rooms", "2 Garden View Rooms"],
  },
  {
    title: "Backwater-Facing Swimming Pool",
    image: PoolImg,
    description: "Perfect for pool parties, relaxation, and lakeside views",
  },
  {
    title: "Dining & Refreshments",
    image: FoodImg,
    items: [
      "Welcome Drinks on Arrival",
      "Evening Tea & Snacks",
      "Sumptuous Dinner (veg/non-veg options available)",
    ],
  },
  {
    title: "Activities",
    image: ActivityImg,
    items: ["Indoor & Outdoor Games", "Lawn Activities & Chill Zones"],
  },
  {
    title: "Natural Bliss",
    image: NaturalBlissImg,
    items: [
      "Sunrise & Sunset by the Lakeside",
      "Calm surroundings and complete tranquility",
    ],
  },
];

const SignatureExperience = () => {
  return (
    <div className="bg-white text-gray-800">
      {/* Hero Section */}
      <section
        className="bg-cover bg-center h-[60vh] flex items-center justify-center text-white text-center px-4"
        style={{ backgroundImage: `url(${signatureBg})` }}
      >
        <div className="bg-black bg-opacity-30 p-6 rounded-xl">
          <h5 className="text-2xl md:text-2xl font-bold mb-2">
            The Signature Experience
          </h5>
          <p className="text-[15px] md:text-[15px]">
            Your Own Private Paradise at Lake n River Resort
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-12 px-6 md:px-20">
        <p className="text-[16px] leading-relaxed max-w-5xl mx-auto text-center">
          Discover the luxury of complete privacy at Lake n River Resort. The
          Signature Experience offers your group exclusive access to the entire
          resort — with 9 premium rooms, a private pool, lakeside views, curated
          dining, and unforgettable sunrises and sunsets. Perfect for private
          getaways, celebrations, or retreats.
        </p>
      </section>

      {/* What's Included */}
      <section className="bg-[#002d11] py-20 px-6 md:px-20 text-white">
        <h4 className="text-3xl md:text-4xl font-bold text-center mb-16">
          We Have Everything You Need
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              className="bg-[#013015] border border-green-700 shadow-lg overflow-hidden flex flex-col text-left"
            >
              {feature.image && (
                <img
                  src={feature.image}
                  alt={feature.title}
                  className="w-full h-60 object-cover"
                  loading="lazy"
                />
              )}
              <div className="p-5 flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {feature.title}
                  </h3>

                  {feature.description && (
                    <p className="text-gray-300 text-sm mb-4">
                      {feature.description}
                    </p>
                  )}

                  {feature.items && (
                    <ul className="list-disc list-inside text-sm text-gray-300 space-y-1 mb-4">
                      {feature.items.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
                {/* <a
                  href="/escape-plan"
                  className="text-green-400 text-sm font-medium hover:underline inline-flex items-center"
                >
                  Read More <span className="ml-1">→</span>
                </a> */}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 px-6 md:px-20 bg-[#013220]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-12 text-center">
            Package Details
          </h2>

          <div className="flex flex-col md:flex-row gap-6 justify-between">
            {/* Card 1 */}
            <div className="flex-1 bg-[#01472C] border border-green-800 text-white rounded-lg shadow-lg p-6">
              <h3 className="text-[1.20rem] font-semibold mb-2">
                Signature Privacy Package
              </h3>
              <p className="text-lg text-gray-300">
                ₹75,999
                <span className="text-sm text-gray-400 ml-3">
                  (Up to 18 guests)
                </span>
              </p>
              {/* <p className="text-sm text-gray-400">Up to 18 guests</p> */}
            </div>

            {/* Card 2 */}
            <div className="flex-1 bg-[#01472C] border border-green-800 text-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold mb-2">Extra Adult</h3>
              <p className="text-lg text-gray-300">₹2,000 per person</p>
            </div>

            {/* Card 3 */}
            <div className="flex-1 bg-[#01472C] border border-green-800 text-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold mb-2">
                Extra Child (5–11 yrs)
              </h3>
              <p className="text-lg text-gray-300">₹1,500 per child</p>
            </div>

            {/* Card 4 */}
            <div className="flex-1 bg-[#01472C] border border-green-800 text-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold mb-2">
                Children below 5 years
              </h3>
              <p className="text-lg text-green-400 font-medium">
                Complimentary
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="bg-[#013220] py-20 px-6 md:px-20">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl font-extrabold text-white mb-6">
            Add-On Experiences 🚣‍♂️
          </h2>
          <p className="text-lg text-gray-300 mb-10">
            Looking for more adventure? Enhance your stay with unforgettable
            water activities.
          </p>

          <div className="flex flex-col md:flex-row gap-6 justify-center text-lg font-medium">
            {/* Canoeing */}
            <div className="flex-1 bg-[#01472C] border border-green-800 text-white rounded-lg shadow p-6 transform transition duration-300 hover:scale-105 hover:shadow-xl hover:border-green-500">
              🌊 <span className="ml-2">Canoeing</span>
            </div>

            {/* Kayaking */}
            <div className="flex-1 bg-[#01472C] border border-green-800 text-white rounded-lg shadow p-6 transform transition duration-300 hover:scale-105 hover:shadow-xl hover:border-green-500">
              🚣‍♀️ <span className="ml-2">Kayaking</span>
            </div>

            {/* Shikkara Boating */}
            <div className="flex-1 bg-[#01472C] border border-green-800 text-white rounded-lg shadow p-6 transform transition duration-300 hover:scale-105 hover:shadow-xl hover:border-green-500">
              🛶 <span className="ml-2">Shikkara Boating</span>
            </div>
          </div>

          <p className="text-[15px] text-gray-400 mt-10 max-w-4xl mx-auto leading-relaxed">
            Our experienced local team will assist and guide your group through
            these backwater adventures. Add-on charges apply.
          </p>
        </div>
      </section>

      {/* Location */}
      <section className="py-16 px-6 md:px-20 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4 tracking-tight">
            <a
              href="https://maps.app.goo.gl/ehbN7Nf51nv8AVe27"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline hover:text-green-600 transition-colors duration-200"
            >
              📍 Location
            </a>
          </h2>

          <div className="w-20 h-[2px] bg-green-600 mx-auto mb-6 rounded-full"></div>
          <p className="text-base md:text-lg text-gray-700 leading-relaxed tracking-wide">
            <span className="font-semibold text-gray-800">
              Lake n River, an SKB Resort – Munroe Island, Kerala.
            </span>
            <br className="hidden md:block" />
            Surrounded by backwaters on three sides, located at the scenic
            confluence of
            <span className="font-medium text-green-700"> Ashtamudi Lake </span>
            and
            <span className="font-medium text-green-700"> Kallada River</span>.
          </p>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="py-16 px-6 md:px-20 bg-[#011c0f] text-white">
        {/* <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">📍 Location</h2> */}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          {/* Address */}
          <div className="flex flex-col items-center">
            <div className="bg-green-500 p-5 rounded-full mb-4">
              <span className="text-white text-3xl">📍</span>
            </div>
            <h3 className="text-xl font-semibold mb-2 text-[#9EEAF9]">
              Our Address
            </h3>
            <p className="text-base text-gray-300 max-w-xs">
              Peninsular Rd, Munroe Island, Kerala 691502
            </p>
          </div>

          {/* Email */}
          <div className="flex flex-col items-center">
            <div className="bg-green-500 p-5 rounded-full mb-4">
              <span className="text-white text-3xl">✉️</span>
            </div>
            <h3 className="text-xl font-semibold mb-2 text-[#9EEAF9]">
              For Reservations
            </h3>
            <p className="text-base text-gray-300 break-words">
              reservations@lakenriver.com
            </p>
          </div>

          {/* Phone */}
          <div className="flex flex-col items-center">
            <div className="bg-green-500 p-5 rounded-full mb-4">
              <span className="text-white text-3xl">📞</span>
            </div>
            <h3 className="text-xl font-semibold mb-2 text-[#9EEAF9]">
              Phone Number
            </h3>
            <p className="text-base text-gray-300">
              +91 8848 207990 | +91 70255 22234
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SignatureExperience;
